
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { useAuthSession } from '@/hooks/useAuthSession';
import { toast } from 'sonner';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { AlertCircle, CheckCircle } from 'lucide-react';
import { checkAffiliateRequestStatus, submitAffiliateRequest } from '@/utils/AffiliateUtils';

const RequestToJoin: React.FC = () => {
  const { session, isLoading } = useAuthSession();
  const [reason, setReason] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [existingRequest, setExistingRequest] = useState<string | null>(null);
  
  useEffect(() => {
    const checkExistingRequest = async () => {
      if (session?.user?.id) {
        const status = await checkAffiliateRequestStatus(session.user.id);
        setExistingRequest(status);
      }
    };
    
    if (!isLoading && session) {
      checkExistingRequest();
    }
  }, [session, isLoading]);
  
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!session?.user) {
      toast.error("You must be logged in to submit a request");
      return;
    }
    
    if (!reason.trim()) {
      setError("Please provide a reason for joining our affiliate program");
      return;
    }
    
    setIsSubmitting(true);
    setError(null);
    
    try {
      // Submit request
      const success = await submitAffiliateRequest(session.user.id, reason);
      
      if (success) {
        toast.success("Your request has been submitted successfully!");
        setExistingRequest('pending');
      } else {
        setError("Failed to submit your request. Please try again later.");
        toast.error("Request submission failed");
      }
    } catch (error: any) {
      console.error('Error submitting request:', error);
      setError("An error occurred. Please try again later.");
      toast.error("Request submission failed");
    } finally {
      setIsSubmitting(false);
    }
  };
  
  const renderRequestForm = () => {
    if (!session) {
      return (
        <div className="text-center p-6 bg-gray-50 rounded-lg">
          <p className="mb-4">Please sign in to request to join our affiliate program.</p>
          <Button asChild>
            <Link to="/auth">Sign In</Link>
          </Button>
        </div>
      );
    }
    
    if (existingRequest) {
      // Different messages based on request status
      let statusMessage = "";
      let statusColor = "";
      let isApproved = false;
      
      if (existingRequest === 'pending') {
        statusMessage = "Your request to join our affiliate program is pending approval. We'll notify you once it's approved.";
        statusColor = "amber";
      } else if (existingRequest === 'approved') {
        statusMessage = "Your request has been approved! You can now apply to become an affiliate.";
        statusColor = "green";
        isApproved = true;
      } else if (existingRequest === 'rejected') {
        statusMessage = "Unfortunately, your request was not approved at this time.";
        statusColor = "red";
      }
      
      return (
        <div className={`p-4 bg-${statusColor}-50 border-l-4 border-${statusColor}-400 rounded-md mb-4`}>
          <p className={`text-${statusColor}-700`}>{statusMessage}</p>
          {isApproved && (
            <Button asChild className="mt-4">
              <Link to="/affiliate/apply">Apply Now</Link>
            </Button>
          )}
        </div>
      );
    }
    
    return (
      <form onSubmit={handleSubmit} className="space-y-4">
        {error && (
          <div className="bg-red-50 border-l-4 border-red-400 p-4">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
              <p className="text-red-700">{error}</p>
            </div>
          </div>
        )}
        
        <div className="space-y-2">
          <Label htmlFor="reason">Why do you want to join our affiliate program?</Label>
          <Textarea
            id="reason"
            placeholder="Tell us about yourself and why you would be a good fit for our affiliate program..."
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={4}
            className="w-full"
          />
        </div>
        
        <Button 
          type="submit" 
          className="w-full" 
          disabled={isSubmitting}
        >
          {isSubmitting ? (
            <><div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent mr-2" /> Submitting...</>
          ) : (
            <><CheckCircle className="h-4 w-4 mr-2" /> Submit Request</>
          )}
        </Button>
      </form>
    );
  };
  
  return (
    <div className="container mx-auto py-8 max-w-3xl">
      <Card className="shadow-lg">
        <CardHeader className="text-center bg-gradient-to-r from-blue-50 to-indigo-50 rounded-t-lg">
          <CardTitle className="text-2xl font-bold text-primary">Affiliate Program</CardTitle>
          <CardDescription>Join our affiliate program and earn rewards</CardDescription>
        </CardHeader>
        <CardContent className="space-y-6 py-6">
          <div className="space-y-4">
            <h3 className="text-lg font-medium">Why become our affiliate?</h3>
            <ul className="list-disc pl-6 space-y-2">
              <li>Earn commissions on every referral that subscribes</li>
              <li>Get access to marketing materials and resources</li>
              <li>Receive timely payments and detailed performance reports</li>
              <li>Join a community of successful affiliates</li>
            </ul>
          </div>

          <div className="space-y-4">
            <h3 className="text-lg font-medium">How it works</h3>
            <ol className="list-decimal pl-6 space-y-2">
              <li>Apply to join our affiliate program</li>
              <li>Once approved, get your unique referral link</li>
              <li>Share your link with your audience</li>
              <li>Earn commissions when people sign up through your link</li>
            </ol>
          </div>

          <div className="bg-blue-50 p-4 rounded-md border border-blue-100">
            <p className="text-sm text-blue-800">
              Our affiliate program offers a competitive commission rate of 20% on all referred subscriptions for the first year.
            </p>
          </div>
          
          <div className="border-t pt-6">
            <h3 className="text-lg font-medium mb-4">Request to Join</h3>
            {isLoading ? (
              <div className="flex justify-center py-4">
                <div className="h-6 w-6 animate-spin rounded-full border-b-2 border-primary"></div>
              </div>
            ) : (
              renderRequestForm()
            )}
          </div>
        </CardContent>
        
        {(!session || (!existingRequest || existingRequest === 'rejected')) && (
          <CardFooter className="flex flex-col space-y-4 sm:flex-row sm:space-y-0 sm:space-x-4 justify-between bg-gray-50 rounded-b-lg">
            <Button asChild variant="outline">
              <Link to="/affiliate/terms">Read Terms</Link>
            </Button>
            {existingRequest === 'approved' && (
              <Button asChild>
                <Link to="/affiliate/apply">Apply Now</Link>
              </Button>
            )}
          </CardFooter>
        )}
      </Card>
    </div>
  );
};

export default RequestToJoin;
